/**
 * React Query persistence configuration
 * 
 * Persists query cache to localStorage for faster app startup after refresh.
 * 
 * What we persist:
 * - Current user (auth/me) - safe, improves UX
 * - Company features - safe, improves UX
 * - Home catalog & prefs - safe, improves UX
 * - Settings (admin/manager/agent) - safe, improves UX
 * - Last visited lists (clients, leads, tasks, contracts) - improves UX, shows cached data instantly
 * - Dashboard overview - improves UX, shows cached data instantly
 * 
 * What we DON'T persist:
 * - Very sensitive data (passwords, tokens - these are never in queries anyway)
 * - Short-lived realtime data (notifications count - too dynamic)
 * - Debug-only data
 * 
 * Note: All persisted data is company-scoped, so switching companies will load fresh data.
 */

import React from 'react';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import { queryClient } from './queryClient';

/**
 * Create localStorage persister
 * Uses localStorage for simplicity (IndexedDB can be used for larger datasets)
 */
export const persister = createSyncStoragePersister({
  storage: typeof window !== 'undefined' ? window.localStorage : undefined,
  key: 'REACT_QUERY_OFFLINE_CACHE',
  // Serialize/deserialize with JSON
  serialize: JSON.stringify,
  deserialize: JSON.parse,
});

/**
 * Filter function to decide which queries to persist
 * 
 * We persist:
 * - auth queries (current user)
 * - company queries (features, settings)
 * - home queries (catalog, prefs)
 * - list queries (clients, leads, tasks, contracts - last visited page)
 * - dashboard overview (not analytics, just overview)
 * - settings queries
 * 
 * We DON'T persist:
 * - notifications (too dynamic)
 * - realtime metrics (too dynamic)
 * - analytics charts (can be regenerated)
 * - Queries in error state (failed requests)
 * - Queries that are pending (not yet resolved)
 */
export function shouldPersistQuery(query: any): boolean {
  const queryKey = query.queryKey as readonly unknown[];
  
  if (!Array.isArray(queryKey) || queryKey.length === 0) {
    return false;
  }

  // CRITICAL: Don't persist queries in error state or pending state
  // This prevents persisting failed 500/503 errors that cause hydration issues
  if (query.state?.status === 'error' || query.state?.status === 'pending') {
    return false;
  }

  // Only persist queries that have successfully loaded data
  if (query.state?.status !== 'success' || !query.state?.data) {
    return false;
  }

  const [domain] = queryKey;
  
  // PHASE 1: Log persistence decisions for leads queries (disabled to reduce console noise)
  // if (domain === 'leads' && process.env.NODE_ENV === 'development') {
  //   const params = queryKey.length >= 4 ? queryKey[3] : undefined;
  //   const hasManagerId = params && typeof params === 'object' && (params as any).managerId;
  //   console.log('[queryPersistence] Evaluating leads query for persistence', {
  //     queryKey,
  //     hasManagerId: !!hasManagerId,
  //     managerId: hasManagerId ? (params as any).managerId : undefined,
  //     willPersist: !hasManagerId, // Will be overridden below if managerId exists
  //   });
  // }

  // Persist these domains
  const persistDomains = [
    'auth',
    'company',
    'home',
    'settings',
    'clients',
    'leads',
    'tasks',
    'orders',
    'managers',
    'agents',
    'dashboard',
    'contracts',
    'appointments',
    'inventory',
    'automations',
    'calendar',
  ];

  if (typeof domain === 'string' && persistDomains.includes(domain)) {
    // Don't persist notifications (too dynamic)
    if (domain === 'notifications') {
      return false;
    }
    
    // CRITICAL FIX: Don't persist ANY leads list queries
    // This prevents stale data from being restored after manager assignment/removal
    // All leads list queries (including leads/my, leads/admin, leads/list) should NOT be persisted
    if (domain === 'leads') {
      // Don't persist leads/admin queries (queryKey format: ['leads','admin',company])
      if (queryKey.length >= 2 && queryKey[1] === 'admin') {
        // Logging disabled to reduce console noise
        return false;
      }
      // Don't persist leads/my queries (queryKey format: ['leads','my',company,managerId])
      if (queryKey.length >= 2 && queryKey[1] === 'my') {
        // Logging disabled to reduce console noise
        return false;
      }
      // Don't persist leads list queries (queryKey[2] === 'list')
      if (queryKey.length >= 3 && queryKey[2] === 'list') {
        // Logging disabled to reduce console noise
        return false;
      }
      // Don't persist managerLeads list queries (queryKey format: ['managerLeads','list',company,managerId,params])
      if (queryKey.length >= 2 && queryKey[0] === 'managerLeads' && queryKey[1] === 'list') {
        // Logging disabled to reduce console noise
        return false;
      }
      // Allow persistence for detail and statuses queries (not list queries)
      return true;
    }
    
    // Don't persist dashboard analytics (can be regenerated)
    if (domain === 'dashboard' && queryKey.length > 2) {
      const subDomain = queryKey[2];
      if (typeof subDomain === 'string' && 
          ['funnel', 'manager-performance', 'bookings', 'tasks-summary', 'payments-summary'].includes(subDomain)) {
        return false; // Don't persist analytics, only overview
      }
    }
    
    return true;
  }

  return false;
}

/**
 * Persisted QueryClient provider component
 * 
 * Usage:
 * ```tsx
 * <PersistedQueryClientProvider>
 *   <App />
 * </PersistedQueryClientProvider>
 * ```
 */
export function PersistedQueryClientProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister,
        maxAge: 1000 * 60 * 60 * 24, // 24 hours - cache expires after 24h
        buster: 'v2-leads-my-fix', // Cache buster - v2-leads-my-fix clears old persisted leads caches that may cause bounce after manager removal
        dehydrateOptions: {
          shouldDehydrateQuery: shouldPersistQuery,
        },
      }}
    >
      {children}
    </PersistQueryClientProvider>
  );
}

